import { Injectable } from '@angular/core';
import { ChatMessage, ChatRole, IAgentResponseDTO, IConversationSettings } from '../models/agent.models';
import { TextEngines, VoiceTTSOptions } from '../models/conversation-enums';
import { convertToHTML } from '../utils/icons/extraction.regex';
import { nanoid } from 'nanoid';

interface ExtraData {
  userImg: string;
  assistantImg: string;
}

@Injectable({
  providedIn: 'root',
})
export class MessageProcessingService {
  // Process message for display
  processMessage(message: ChatMessage, conversationSettings: IConversationSettings, extraData: ExtraData = {} as ExtraData): ChatMessage {
    let processedMessage: ChatMessage = { ...message };

    // Add ID if not present
    if (!processedMessage.messageId) {
      processedMessage.messageId = nanoid();
    }

    // Add images and default voice

    if (message.role === ChatRole.User && extraData.userImg) {
      processedMessage.imgUrl = extraData.userImg;
    } else if (message.role === ChatRole.Assistant && extraData.assistantImg) {
      // TODO i think lessons send voice in voice, but should be in tts
      const defaultVoice = this.getVoice(conversationSettings?.tts?.voice || conversationSettings.voice);
      processedMessage.voice = defaultVoice;

      processedMessage.imgUrl = extraData.assistantImg;
      // I think Process Text Engine should only apply to assistant messages not user
      // Process based on text engine
      if (conversationSettings.textEngine === TextEngines.MarkdownMultiMessages) {
        this.processMultiMessages(processedMessage, conversationSettings);
      } else if (conversationSettings.textEngine === TextEngines.MarkdownSSML) {
        // TODO: check how to add default secondary voice
        if (!conversationSettings?.tts?.secondaryVoice) {
          throw new Error('Secondary voice is required for SSML');
        }
        const content = this.subsItalicsByTag(processedMessage.content, conversationSettings.tts.secondaryVoice);
        processedMessage.ssml = '<speak>' + content + '</speak>';
      }
    }

    return processedMessage;
  }

  // Process multi-messages from markdown
  private processMultiMessages(message: ChatMessage, settings: IConversationSettings): void {
    // Convert markdown to HTML segments
    const mdToHtml = convertToHTML(message.content);

    // Map segments to multi-messages
    message.multiMessages = mdToHtml.map((val) => {
      // Get the narrator voice from conversation settings
      const narratorVoice = settings.tts?.secondaryVoice || 'en-US-News-L';

      // Determine if this is italicized text (narrator)
      const isItalics = val.tag === 'em';
      const voice = isItalics ? narratorVoice : message.voice;

      return {
        voice,
        content: val.content,
        audioUrl: null,
        audioPromise: null,
        text: val.text,
        tag: val.tag,
      };
    });
  }

  // Replace italics with voice tags for SSML
  subsItalicsByTag(text: string, voiceId = 'it-IT-Neural2-A', tagName = 'voice'): string {
    const regex = /\*(.*?)\*/g;
    return text.replace(regex, (match, p1) => `<${tagName} name="${voiceId}">${p1}</${tagName}>`);
  }

  // Get appropriate voice based on settings
  getVoice(voice_value: string, targetLang = 'en'): string {
    if ([null, '', 'random'].includes(voice_value)) {
      const voiceCodes: string[] = VoiceTTSOptions.map((val) => val.id);
      return voiceCodes[Math.floor(Math.random() * voiceCodes.length)];
    }

    if (voice_value === 'randomMan') {
      const voiceCodes = VoiceTTSOptions.filter((voice) => voice.gender === 'male' && voice.lang.includes(targetLang)).map((val) => val.id);
      return voiceCodes[Math.floor(Math.random() * voiceCodes.length)];
    } else if (voice_value === 'randomWoman') {
      const voiceCodes = VoiceTTSOptions.filter((voice) => voice.gender === 'female' && voice.lang.includes(targetLang)).map((val) => val.id);
      return voiceCodes[Math.floor(Math.random() * voiceCodes.length)];
    } else {
      return voice_value;
      // voice should exist, there wont be validation.
      // const voice = VoiceTTSOptions.find((voice) => voice.id === voice_value);
      // if (voice) {
      //   return voice.id;
      // } else {
      //   console.error('Voice not found getting something random', voice_value);
      //   return VoiceTTSOptions.find((voice) => voice.lang.includes(targetLang))?.id || '';
      // }
    }
  }
}
